import { DISCOUNT_MODES } from "../../../../data/constants";
import { removeExternalDiscount } from "../../../../helpers/saleHelpers";
import { isCartDeletionScenario } from "../cartEventEffect/cartEventHandler";
import { selectActiveLoyaltyProvider } from "../../../selectors/loyaltySelectors";
import {
  getLoyaltyProviderForDiscount,
  isExternalLoyaltyProvider,
  isLoyaltyPointDiscountType,
  loyaltyRefundHelper,
} from "../../../../helpers/loyaltyHelper";
import { getLastOrderPlacedMid } from "../../../../helpers/cartAuditHelper";
import { isOffline } from "../../../../facade/ServiceFacade";

/**
 * WEB-ONLY module — `redux-store/middleware/saleUpdate/resetSaleEffect`.
 *
 * 🔴 **This one is a KNOWN GAP, not a decision, and it is blocked on something specific.**
 * It owns half of `docs/flows/LOYALTY_REFUND.md`'s core invariant: points redeemed against a
 * sale that is then DISCARDED have to be unblocked, or they stay blocked server-side with no
 * bill left to release them. Mobile runs the rest of the shared loyalty flow (and, since
 * 2026-09-07, `../loyaltyRefundEffect`) but not this, so a discarded mobile cart still strands
 * the customer's points.
 *
 * ⚠️ **Do not simply register it in `../index.native.ts`.** Investigated 2026-09-07; two things
 * break, and the second is a money bug:
 *
 *  1. `getLastOrderPlacedMid` does not exist on that platform. `helpers/cartAuditHelper`'s
 *     native variant is a deliberately EMPTY shell — there is no cart-audit telemetry there —
 *     so importing the name is exactly the bug its own header says the typechecker will catch.
 *  2. **Neither signal that distinguishes a discard from a completed order exists there.**
 *     `saleValidated` is a WEB-ONLY field: `SaleSlice`'s desktop `saveSaleData` destructures it
 *     off the payload into its own key, the native one stores the payload whole, and the only
 *     writer is web's `customerAndDeliveryInfoValidation`. The order-placed marker is the
 *     web-only cart-audit state from (1). So on mobile `isCartDeletionScenario` collapses to
 *     "the cart went from non-empty to empty" — which is ALSO what placing an order looks like,
 *     and `refundLoyaltyPointsOnDiscard` would then refund points the order legitimately
 *     consumed. Stubbing the marker does not help; it removes the guard that is doing the work.
 *
 * Sharing this therefore means giving that platform a real "this sale was placed" signal first
 * — not moving `isCartDeletionScenario` out of `../cartEventEffect/cartEventHandler`, which is a
 * pure three-argument predicate that could move any time and was never the real obstacle. The
 * external-discount half has its own smaller blocker: `removeExternalDiscount` is likewise
 * desktop-only in `helpers/saleHelpers`.
 *
 * ⚠️ **A dead import was dropped** (rule 2b / hazard 13): the app file imported
 * `EXTERNAL_LOYALTY_PARTNERS` from `data/constants` and never referenced it. The external-provider
 * test that reads as if it would use it actually goes through `isExternalLoyaltyProvider`.
 */

/**
 * Listener effect for saveSaleData action.
 *
 * Responsibilities:
 *  - Keep cart audit session synced with current cart value
 *  - Fire CART_DELETED when an existing cart is cleared into a new sale
 *  - Clean up external loyalty discounts (XENO) on cart deletion
 *  - Unblock redeemed loyalty points on cart deletion
 *
 * All cart audit conditions are handled inside cartEventHandler
 * this effect just delegates and handles non-cart-audit cleanup.
 */

export const resetSaleEffect = async (action: any, listenerApi: any) => {
  const prevState = listenerApi.getOriginalState();
  const currentState = listenerApi.getState();

  const prevSale = prevState?.saleDataStore?.saleData;

  // External loyalty discount cleanup (XENO, BLUETOKAI) — runs on same condition
  const currentSale = currentState?.saleDataStore?.saleData;
  const saleValidated = prevState?.saleDataStore?.saleValidated;
  if (isCartDeletionScenario(prevSale, currentSale, saleValidated)) {
    const billDiscountList = prevSale?.billDiscountList || [];
    billDiscountList.forEach((discount: any) => {
      if (discount.mode !== DISCOUNT_MODES.auto && !discount.appliedBy) {
        const externalProviderData = selectActiveLoyaltyProvider(currentState);
        if (isExternalLoyaltyProvider(externalProviderData?.metaId)) {
          removeExternalDiscount(prevSale, discount);
        }
      }
    });

    refundLoyaltyPointsOnDiscard(prevSale, currentState);
  }
};

/**
 * Unblocks loyalty points that were redeemed on a sale being discarded.
 *
 * Only for discarded sales: a sale that was actually placed as an order legitimately consumed
 * its points, so it must not be refunded. `isCartDeletionScenario` (checked by the caller)
 * covers validation state; the order-placed marker additionally excludes carts that were just
 * submitted, mirroring handleCartDeleted.
 *
 * Depends on this effect running before cartEventEffect, whose handleCartDeleted clears the
 * order-placed marker after use — see the effect order in saleUpdate/index.desktop.ts.
 */
function refundLoyaltyPointsOnDiscard(prevSale: any, currentState: any) {
  const loyaltyDiscount = prevSale?.billDiscountList?.find(isLoyaltyPointDiscountType);
  if (!loyaltyDiscount) return;
  if (isOffline()) return; // loyalty refund is server-backed
  if (getLastOrderPlacedMid() === prevSale?.mid) return; // cart was placed as an order

  const loyaltyProviders = currentState?.businessStore?.businessData?.loyaltyProviders || [];
  const provider: any = getLoyaltyProviderForDiscount(loyaltyProviders, loyaltyDiscount);

  // skipSave: the sale is being discarded, so the refunded sale must not overwrite the new
  // one. prevSale is passed explicitly because the store already holds the new sale.
  loyaltyRefundHelper(provider?.mid, undefined, true, prevSale);
}
